import React, { useEffect, useMemo, useState } from "react"

import { Divider } from "antd"

import { IOneVerifyOrganizationUser } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { TagsSelectElement } from "@front-shared/elements/tagsSelect.element.tsx"
import { useStateCallback } from "@front-shared/hooks/useStateCallback.hook.ts"
import type { ISelectRawOption } from "@front-shared/types/objects.types.ts"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { organizationSelector } from "../../../store/selectors/organization.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { organizationUsersGetList } from "../../../store/thunks/organization.thunks"

import plusIcon from "@front-shared/assets/images/icons/plus-dark.svg"

export const BillerGroupUsersAddComponent: React.FunctionComponent<
    IBillerGroupUsersAddComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const organizationState = useAppSelector(organizationSelector)

    const [selectKey, setSelectKey] = useState<number>(new Date().getTime())
    const [selectedUserIds, setSelectedUserIds] = useStateCallback<number[]>([])

    // On mount load all users from backend if not already loaded
    useEffect(() => {
        if (organizationState.users.length || !selectedPracticeRole) {
            return
        }

        dispatch(
            organizationUsersGetList({
                payload: undefined,
                practice: selectedPracticeRole.practice,
            }),
        )
    }, [selectedPracticeRole?.id])

    const getUserLabelValue = (user: IOneVerifyOrganizationUser): string => {
        return user.name || user.id.toString()
    }

    const availableUserOptions = useMemo<ISelectRawOption[]>(() => {
        return organizationState.users
            .filter(
                user =>
                    !props.existingUsers.map(item => item.id).includes(user.id),
            )
            .sort((a, b) => {
                // Sort function that sorts by name, pushing missing names to the end unsorted
                return !a.name ? 1 : !b.name ? -1 : a.name.localeCompare(b.name)
            })
            .map(user => ({
                label: getUserLabelValue(user),
                value: user.id,
            }))
    }, [props.existingUsers, organizationState.users])

    const getCustomUserSelectContent = (menu: JSX.Element): JSX.Element => {
        return (
            <div className="pb-[24px] relative">
                {menu}

                <Divider style={{ margin: "8px 0" }} />

                <a
                    href={`${ROUTES_CONFIG.organizationUrl}?${GENERAL_CONFIG.urlSearchParamsKeys.tab}=users&${GENERAL_CONFIG.urlSearchParamsKeys.create}=true`}
                    target="_blank"
                    rel="noreferrer"
                >
                    <div className="p-[8px] cursor-pointer flex items-center absolute z-[10] bottom-0 left-0 w-full bg-white hover:bg-gray-100">
                        <img className="mr-[8px]" src={plusIcon} alt="Add" />

                        <PrimaryText typography="h6">
                            Add a new user
                        </PrimaryText>
                    </div>
                </a>
            </div>
        )
    }

    const onUserSelected = (userIds: number[]) => {
        setSelectedUserIds(userIds)
    }

    const reset = () => {
        setSelectedUserIds([])

        // This will give warning in console
        // Related to some antd internals, not affecting our app
        setSelectKey(new Date().getTime())
    }

    const onSubmitSelectedUsers = () => {
        // Pass the entire user object to the parent component, not just the id
        const users = organizationState.users.filter(item =>
            selectedUserIds.includes(item.id),
        )

        props.onUsersAdded(users)

        reset()
    }

    return (
        <div
            key={selectKey}
            className={`
                relative flex items-center
                ${props.className}
            `}
        >
            <TagsSelectElement
                className="flex-1 mr-[24px]"
                placeholder="Add team member(s)"
                name="users"
                onSelected={items => {
                    onUserSelected(items.map(item => +item))
                }}
                maxTagCount={6}
                options={availableUserOptions}
                dropdownRender={getCustomUserSelectContent}
            />

            <ButtonElement
                label="Add"
                htmlType="button"
                type="default"
                onClick={onSubmitSelectedUsers}
                disabled={!selectedUserIds.length}
            />
        </div>
    )
}

interface IBillerGroupUsersAddComponentProps {
    className?: string
    existingUsers: IOneVerifyOrganizationUser[]
    onUsersAdded: (users: IOneVerifyOrganizationUser[]) => void
}
